import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { trpc } from '@/lib/trpc';

/**
 * Paste a chart back in from a spreadsheet.
 *
 * Export already exists; there was no way back, and accountants plan in Excel.
 * Rows match on code — an existing code renames, a new one is created with its
 * missing parents materialized. Nothing is written until the preview is
 * reviewed, because a paste can carry hundreds of rows.
 */

interface ImportAccountsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ParsedRow = { code: string; name: string; type?: AccountType };
type AccountType = 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';

const TYPES = new Set(['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE']);

/**
 * Parse pasted rows: tab- or semicolon-separated (what Excel puts on the
 * clipboard), `code<sep>name<sep>type?`. Blank lines and an obvious header row
 * are skipped rather than reported as errors.
 */
function parsePaste(text: string): { rows: ParsedRow[]; skipped: number } {
  const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0);
  const rows: ParsedRow[] = [];
  let skipped = 0;

  for (const line of lines) {
    const cells = line.split(/\t|;/).map((cell) => cell.trim());
    const [code, name, type] = cells;
    if (!code || !name) {
      skipped += 1;
      continue;
    }
    // Header row: "Kod / Code" in the first cell.
    if (/^(kod|code|hesap kodu)$/i.test(code)) {
      skipped += 1;
      continue;
    }
    const upper = (type ?? '').toUpperCase();
    rows.push({ code, name, ...(TYPES.has(upper) ? { type: upper as AccountType } : {}) });
  }

  return { rows, skipped };
}

export function ImportAccountsDialog({ open, onOpenChange }: ImportAccountsDialogProps) {
  const { t } = useTranslation('accounting');
  const [text, setText] = useState('');
  const utils = trpc.useUtils();

  const preview = trpc.accountWorkbench.importPreview.useMutation();
  const apply = trpc.accountWorkbench.importApply.useMutation({
    onSuccess: () => {
      utils.account.getTree.invalidate();
      utils.account.list.invalidate();
      utils.accountWorkbench.health.invalidate();
      utils.accountWorkbench.changes.invalidate();
      setText('');
      preview.reset();
      onOpenChange(false);
    },
  });

  const parsed = parsePaste(text);
  const result = preview.data;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-w-2xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t('import.title')}</DialogTitle>
          <DialogDescription>{t('import.description')}</DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
          <div className="space-y-2">
            <Textarea
              value={text}
              onChange={(event) => {
                setText(event.target.value);
                preview.reset();
              }}
              rows={8}
              className="font-mono text-xs"
              placeholder={'255.01\tBURO DEMIRBASLARI\tASSET\n255.01.01\tMERKEZ BURO'}
            />
            <p className="text-muted-foreground text-xs">
              {t('import.formatHint', { count: parsed.rows.length })}
            </p>
          </div>

          {result && (
            <div className="border-border divide-border divide-y border text-sm">
              <div className="grid grid-cols-3 divide-x">
                <div className="p-3">
                  <span className="block text-lg font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">
                    {result.creates.length}
                  </span>
                  <span className="text-muted-foreground text-xs">{t('import.willCreate')}</span>
                </div>
                <div className="p-3">
                  <span className="block text-lg font-semibold tabular-nums text-amber-700 dark:text-amber-400">
                    {result.updates.length}
                  </span>
                  <span className="text-muted-foreground text-xs">{t('import.willRename')}</span>
                </div>
                <div className="p-3">
                  <span className="text-muted-foreground block text-lg font-semibold tabular-nums">
                    {result.unchanged}
                  </span>
                  <span className="text-muted-foreground text-xs">{t('import.unchanged')}</span>
                </div>
              </div>

              {result.updates.length > 0 && (
                <div className="max-h-40 overflow-y-auto p-3">
                  <p className="mb-1.5 text-xs font-medium">{t('import.renames')}</p>
                  <ul className="space-y-0.5 font-mono text-xs">
                    {result.updates.slice(0, 30).map((update) => (
                      <li key={update.code} className="text-muted-foreground">
                        <span className="text-foreground">{update.code}</span> · {update.from} →{' '}
                        {update.to}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.problems.length > 0 && (
                <div className="bg-amber-50 p-3 dark:bg-amber-950/30">
                  <ul className="list-inside list-disc space-y-0.5 text-xs text-amber-800 dark:text-amber-300">
                    {result.problems.slice(0, 10).map((problem) => (
                      <li key={problem}>{problem}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {(preview.error || apply.error) && (
            <p className="bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
              {preview.error?.message ?? apply.error?.message}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('accountModal.cancel')}
          </Button>
          {result ? (
            <Button
              disabled={apply.isPending || parsed.rows.length === 0}
              onClick={() => apply.mutate({ rows: parsed.rows })}
            >
              {apply.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('import.apply', {
                count: result.creates.length + result.updates.length,
              })}
            </Button>
          ) : (
            <Button
              disabled={preview.isPending || parsed.rows.length === 0}
              onClick={() => preview.mutate({ rows: parsed.rows })}
            >
              {preview.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('import.preview')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
